import React from "react";

import {
  FiBox,
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiRepeat,
  FiTrendingDown,
  FiTrendingUp,
} from "react-icons/fi";

const STOCK_IN_TYPES = [
  "opening_stock",
  "stock_in",
  "sale_cancelled",
  "customer_return",
  "adjustment_in",
];

const MOVEMENT_LABELS = {
  opening_stock: "Opening Stock",
  stock_in: "Stock In",
  stock_out: "Stock Out",
  sale: "Sale",
  sale_cancelled: "Sale Cancelled",
  customer_return: "Customer Return",
  supplier_return: "Supplier Return",
  damaged: "Damaged",
  lost: "Lost",
  adjustment_in: "Adjustment In",
  adjustment_out: "Adjustment Out",
};

const REFERENCE_LABELS = {
  product: "Product",
  purchase: "Purchase",
  order: "Order",
  return: "Return",
  manual: "Manual",
  other: "Other",
};

const StockMovementTable = ({
  movements = [],
  loading = false,
  pagination,
  onPageChange,
  onLimitChange,
  onOpenHistory,
  onAdjustStock,
  onReconcileStock,
}) => {
  const safePagination = {
    page: Number(pagination?.page || 1),
    limit: Number(pagination?.limit || 10),
    total: Number(pagination?.total || 0),
    totalPages: Math.max(
      Number(pagination?.totalPages || 1),
      1
    ),
  };

  const formatDate = (value) => {
    if (!value) {
      return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return new Intl.DateTimeFormat("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(Number(value || 0));
  };

  const getSerialNumber = (index) => {
    return (
      (safePagination.page - 1) *
        safePagination.limit +
      index +
      1
    );
  };

  const isStockIn = (movementType) => {
    return STOCK_IN_TYPES.includes(
      movementType
    );
  };

  const getMovementLabel = (
    movementType
  ) => {
    return (
      MOVEMENT_LABELS[movementType] ||
      String(movementType || "-")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (character) =>
          character.toUpperCase()
        )
    );
  };

  const getReferenceLabel = (
    referenceType
  ) => {
    return (
      REFERENCE_LABELS[referenceType] ||
      "-"
    );
  };

  const getProduct = (movement) => {
    if (
      movement?.product &&
      typeof movement.product ===
        "object"
    ) {
      return movement.product;
    }

    return null;
  };

  return (
    <div className="inventory-stock-table-card">
      <div className="inventory-stock-table-scroll">
        <table className="inventory-stock-table">
          <thead>
            <tr>
              <th style={{ width: 70 }}>
                S.No
              </th>

              <th>
                Product
              </th>

              <th>
                Movement
              </th>

              <th>
                Quantity
              </th>

              <th>
                Stock Change
              </th>

              <th>
                Reference
              </th>

              <th>
                Purchase Price
              </th>

              <th>
                Supplier
              </th>

              <th>
                Performed By
              </th>

              <th>
                Date
              </th>

              <th style={{ width: 170 }}>
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={11}
                  className="inventory-stock-table-message"
                >
                  <div className="inventory-stock-table-loading">
                    <span className="inventory-stock-table-spinner" />

                    Loading stock movements...
                  </div>
                </td>
              </tr>
            ) : movements.length === 0 ? (
              <tr>
                <td
                  colSpan={11}
                  className="inventory-stock-table-message"
                >
                  <div className="inventory-stock-table-empty">
                    <FiRepeat />

                    <strong>
                      No Stock Movements Found
                    </strong>

                    <span>
                      Stock transactions will
                      appear here after stock is
                      added, reduced, sold,
                      returned, damaged or
                      reconciled.
                    </span>
                  </div>
                </td>
              </tr>
            ) : (
              movements.map(
                (movement, index) => {
                  const product =
                    getProduct(movement);

                  const stockIn =
                    isStockIn(
                      movement?.movementType
                    );

                  return (
                    <tr
                      key={
                        movement?._id ||
                        index
                      }
                    >
                      <td className="inventory-stock-table-serial">
                        {getSerialNumber(
                          index
                        )}
                      </td>

                      <td>
                        <div className="inventory-stock-table-product">
                          <div className="inventory-stock-table-product-icon">
                            <FiBox />
                          </div>

                          <div>
                            <strong
                              title={
                                product?.name
                              }
                            >
                              {product?.name ||
                                "Unknown Product"}
                            </strong>

                            <span>
                              {product?.sku ||
                                "-"}
                            </span>

                            {product?.barcode ? (
                              <small>
                                {
                                  product.barcode
                                }
                              </small>
                            ) : null}
                          </div>
                        </div>
                      </td>

                      <td>
                        <span
                          className={`inventory-stock-movement-badge ${
                            stockIn
                              ? "stock-in"
                              : "stock-out"
                          }`}
                        >
                          {stockIn ? (
                            <FiTrendingUp />
                          ) : (
                            <FiTrendingDown />
                          )}

                          {getMovementLabel(
                            movement?.movementType
                          )}
                        </span>
                      </td>

                      <td>
                        <div className="inventory-stock-quantity">
                          <strong>
                            {stockIn
                              ? "+"
                              : "-"}
                            {Number(
                              movement?.quantity ||
                                0
                            )}
                          </strong>
                        </div>
                      </td>

                      <td>
                        <div className="inventory-stock-change">
                          <span>
                            Previous
                          </span>

                          <strong>
                            {Number(
                              movement?.previousQty ||
                                0
                            )}
                          </strong>

                          <span>
                            New
                          </span>

                          <strong>
                            {Number(
                              movement?.newQty ||
                                0
                            )}
                          </strong>
                        </div>
                      </td>

                      <td>
                        <div className="inventory-stock-reference">
                          <strong>
                            {getReferenceLabel(
                              movement?.referenceType
                            )}
                          </strong>

                          <span>
                            {movement?.referenceNumber ||
                              "-"}
                          </span>
                        </div>
                      </td>

                      <td>
                        <span className="inventory-stock-purchase-price">
                          {Number(
                            movement?.purchasePrice ||
                              0
                          ) > 0
                            ? formatCurrency(
                                movement.purchasePrice
                              )
                            : "-"}
                        </span>
                      </td>

                      <td>
                        <div className="inventory-stock-supplier">
                          <strong>
                            {movement?.supplierName ||
                              "-"}
                          </strong>

                          <span
                            title={
                              movement?.notes
                            }
                          >
                            {movement?.notes ||
                              "No notes"}
                          </span>
                        </div>
                      </td>

                      <td>
                        <div className="inventory-stock-user">
                          <strong>
                            {movement?.performedBy
                              ?.name ||
                              "System"}
                          </strong>

                          <span>
                            {movement?.performedBy
                              ?.role ||
                              "-"}
                          </span>
                        </div>
                      </td>

                      <td>
                        <span className="inventory-stock-created-date">
                          {formatDate(
                            movement?.createdAt
                          )}
                        </span>
                      </td>

                      <td>
                        <div className="inventory-stock-actions">
                          <button
                            type="button"
                            className="inventory-stock-action inventory-stock-action-history"
                            title="View stock history"
                            disabled={!product}
                            onClick={() =>
                              product &&
                              onOpenHistory(
                                product
                              )
                            }
                          >
                            <FiEye />
                          </button>

                          <button
                            type="button"
                            className="inventory-stock-action inventory-stock-action-adjust"
                            title="Adjust stock"
                            disabled={!product}
                            onClick={() =>
                              product &&
                              onAdjustStock(
                                product
                              )
                            }
                          >
                            <FiBox />
                          </button>

                          <button
                            type="button"
                            className="inventory-stock-action inventory-stock-action-reconcile"
                            title="Reconcile stock"
                            disabled={!product}
                            onClick={() =>
                              product &&
                              onReconcileStock(
                                product
                              )
                            }
                          >
                            <FiRepeat />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }
              )
            )}
          </tbody>
        </table>
      </div>

      {!loading &&
      safePagination.total > 0 ? (
        <div className="inventory-stock-pagination">
          <div className="inventory-stock-pagination-summary">
            Showing{" "}
            <strong>
              {(safePagination.page - 1) *
                safePagination.limit +
                1}
            </strong>{" "}
            to{" "}
            <strong>
              {Math.min(
                safePagination.page *
                  safePagination.limit,
                safePagination.total
              )}
            </strong>{" "}
            of{" "}
            <strong>
              {safePagination.total}
            </strong>{" "}
            movements
          </div>

          <div className="inventory-stock-pagination-controls">
            <select
              value={safePagination.limit}
              onChange={onLimitChange}
              aria-label="Movements per page"
            >
              <option value={10}>
                10 / page
              </option>

              <option value={20}>
                20 / page
              </option>

              <option value={50}>
                50 / page
              </option>

              <option value={100}>
                100 / page
              </option>
            </select>

            <button
              type="button"
              disabled={
                safePagination.page <= 1
              }
              onClick={() =>
                onPageChange(
                  safePagination.page -
                    1
                )
              }
              title="Previous page"
            >
              <FiChevronLeft />
            </button>

            <span>
              Page{" "}
              <strong>
                {safePagination.page}
              </strong>{" "}
              of{" "}
              <strong>
                {
                  safePagination.totalPages
                }
              </strong>
            </span>

            <button
              type="button"
              disabled={
                safePagination.page >=
                safePagination.totalPages
              }
              onClick={() =>
                onPageChange(
                  safePagination.page +
                    1
                )
              }
              title="Next page"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default StockMovementTable;